import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import type { Currency } from '#src/__generated__/globalTypes.ts';
import { UserAccountEntryEntry } from '#src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconArrowDownLeft } from '#src/atoms/icons/system/iconArrowDownLeft.ts';
import { AccountEntry } from '#src/components/mySorare/TransactionsHistory/UserAccountEntries/AccountEntry/index.tsx';
import { glossary } from '#src/lib/glossary.ts';

import type { Deposit_userAccountEntry } from './__generated__/index.graphql.ts';

const DepositIcon = styled(FontAwesomeIcon)`
  width: var(--double-unit);
  height: var(--double-unit);
  border-radius: 50%;
  align-self: center;
  background-color: transparent;
  border: 1px solid var(--c-onBase-success);
  color: var(--c-onBase-success);
  padding: var(--unit);
`;

type Props = {
  userAccountEntry: Deposit_userAccountEntry;
  withIcon?: boolean;
  expandable?: boolean;
  primaryCurrency: Currency;
};

export const Deposit = ({
  userAccountEntry,
  withIcon = false,
  expandable,
  primaryCurrency,
}: Props) => {
  const { formatMessage } = useIntl();

  if (userAccountEntry.entryType !== UserAccountEntryEntry.DEPOSIT) return null;

  return (
    <AccountEntry
      userAccountEntry={userAccountEntry}
      title={formatMessage(glossary.deposit)}
      expandable={expandable}
      icon={withIcon ? <DepositIcon icon={iconArrowDownLeft} /> : undefined}
      primaryCurrency={primaryCurrency}
    />
  );
};

Deposit.fragments = {
  userAccountEntry: gql`
    fragment Deposit_userAccountEntry on UserAccountEntry {
      id
      entryType
      ...AccountEntry_userAccountEntry
    }
    ${AccountEntry.fragments.userAccountEntry}
  ` as TypedDocumentNode<Deposit_userAccountEntry>,
};
